import React from "react";
import { createRoot } from "react-dom/client";
import {
  ThemeProvider,
  StyledEngineProvider,
  createTheme
} from "@mui/material/styles";
import { StoreProvider, useStoreState } from "easy-peasy";
import { store } from "./store";
import App from "./components/App";
import { CssBaseline } from "@mui/material";

// Shared, theme-agnostic config. Component styleOverrides reference
// var(--teleforce-*) tokens, which flip automatically via the <html data-theme>
// attribute, so the same block works for both light and dark.
const sharedTheme = {
  typography: {
    fontFamily: "teleforce-sans, sans-serif, system-ui"
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "var(--teleforce-750)"
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--teleforce-800)",
          backgroundImage: "initial"
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "var(--teleforce-500) !important"
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            backgroundColor: "var(--teleforce-750)",
            color: "var(--teleforce-700)"
          }
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "var(--teleforce-725)"
          },
          "&.Mui-focusVisible": {
            backgroundColor: "var(--teleforce-725)"
          }
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          background: "var(--teleforce-975)",
          border: "1px solid var(--teleforce-750)",
          borderRadius: "5px !important",

          "&:hover": {
            background: "var(--teleforce-950)"
          },
          "&.Mui-focused": {
            background: "var(--teleforce-950)"
          }
        },
        underline: {
          "&:before, &:hover:before": {
            borderBottom: "0 !important"
          },
          "&:after": {
            borderBottomColor: "var(--teleforce-700) !important"
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: "var(--teleforce-725)"
        },
        notchedOutline: {
          borderColor: "var(--teleforce-725)"
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "var(--teleforce-700)"
          }
        }
      }
    }
  }
};

const sharedPalette = {
  primary: {
    main: "#2196f3" // Blue
  },
  secondary: {
    main: "#dc004e"
  }
};

const darkTheme = createTheme({
  ...sharedTheme,
  palette: {
    ...sharedPalette,
    mode: "dark",
    default: {
      main: "#fff",
      dark: "#d1d1d4",
      contrastText: "#000000DE"
    },
    background: {
      paper: "var(--teleforce-app-bg)",
      default: "var(--teleforce-app-bg)"
    }
  }
});

const lightTheme = createTheme({
  ...sharedTheme,
  palette: {
    ...sharedPalette,
    mode: "light",
    default: {
      main: "#3d3d42",
      dark: "#2c2c2f",
      contrastText: "#fff"
    },
    background: {
      paper: "var(--teleforce-800)",
      default: "var(--teleforce-app-bg)"
    }
  }
});

// Reads the persisted theme mode from the store and applies the matching MUI
// theme. The <html data-theme> attribute (set pre-paint in index.html and kept
// in sync by the store) drives the CSS variables in parallel.
function ThemedRoot() {
  const themeMode = useStoreState(state => state.settings.themeMode);
  const theme = themeMode === "light" ? lightTheme : darkTheme;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <StoreProvider store={store}>
    <ThemedRoot />
  </StoreProvider>
);
